import React, { useEffect, useMemo, useState } from 'react';
import { sdk } from '../../lib/sdk';
import { getErrorMessage } from '../../lib/utils/errorMessage';

type Schedule = {
  id?: string;
  _id: string;
  nextRunAt?: string;
  isActive: boolean;
  timezone: string;
  frequencyType: 'daily' | 'weekly' | 'cron';
  hour?: number;
  minute?: number;
  weekdays?: number[];
  cronExpression?: string;
  recipientEmails: string[];
  reportConfig?: {
    reportType: 'donation' | 'expense' | 'both';
    dateWindow: {
      strategy: 'last_n_days' | 'fixed_range';
      days?: number;
      startDate?: string;
      endDate?: string;
    };
    delivery: {
      formats: Array<'pdf' | 'excel' | 'csv'>;
      mode: 'attachment' | 'link' | 'both';
    };
  };
};

type FormState = {
  frequencyType: 'daily' | 'weekly' | 'cron';
  timezone: string;
  hour: number;
  minute: number;
  weekdays: number[];
  cronExpression: string;
  recipientEmails: string[];
  recipientInput: string;
  reportType: 'donation' | 'expense' | 'both';
  dateWindowStrategy: 'last_n_days' | 'fixed_range';
  days: number;
  startDate: string;
  endDate: string;
  formats: Array<'pdf' | 'excel' | 'csv'>;
  mode: 'attachment' | 'link' | 'both';
  isActive: boolean;
};

const defaultFormState: FormState = {
  frequencyType: 'daily',
  timezone: 'Asia/Karachi',
  hour: 9,
  minute: 0,
  weekdays: [1],
  cronExpression: '0 9 * * *',
  recipientEmails: [],
  recipientInput: '',
  reportType: 'both',
  dateWindowStrategy: 'last_n_days',
  days: 7,
  startDate: '',
  endDate: '',
  formats: ['pdf'],
  mode: 'attachment',
  isActive: true,
};

const weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const resolveErrorMessage = (error: any, fallback: string): string => {
  const code = error?.response?.data?.code;
  if (code === 'ROUTE_NOT_FOUND') {
    return 'Scheduled reports API is not available in this environment yet.';
  }
  return getErrorMessage(error, fallback);
};

const toTimeString = (hour: number, minute: number): string => {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

const parseTimeString = (value: string): { hour: number; minute: number } => {
  const [rawHour = '0', rawMinute = '0'] = value.split(':');
  const nextHour = Number(rawHour);
  const nextMinute = Number(rawMinute);
  return {
    hour: Number.isFinite(nextHour) ? Math.min(Math.max(nextHour, 0), 23) : 0,
    minute: Number.isFinite(nextMinute) ? Math.min(Math.max(nextMinute, 0), 59) : 0,
  };
};

const extractList = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const ScheduledReports: React.FC = () => {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(defaultFormState);

  const sortedSchedules = useMemo(
    () => [...schedules].sort((a, b) => new Date(a.nextRunAt || 0).getTime() - new Date(b.nextRunAt || 0).getTime()),
    [schedules]
  );

  const fetchSchedules = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await sdk.client.get('/api/v1/automation/reports/schedules');
      setSchedules(extractList(response));
    } catch (e: any) {
      setError(resolveErrorMessage(e, 'Failed to load schedules'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchedules();
  }, []);

  const addRecipient = () => {
    const email = form.recipientInput.trim().toLowerCase();
    if (!email) return;
    if (form.recipientEmails.includes(email)) {
      setForm((prev) => ({ ...prev, recipientInput: '' }));
      return;
    }
    setForm((prev) => ({ ...prev, recipientEmails: [...prev.recipientEmails, email], recipientInput: '' }));
  };

  const removeRecipient = (email: string) => {
    setForm((prev) => ({
      ...prev,
      recipientEmails: prev.recipientEmails.filter((value) => value !== email),
    }));
  };

  const toggleFormat = (format: 'pdf' | 'excel' | 'csv') => {
    setForm((prev) => {
      const exists = prev.formats.includes(format);
      const next = exists ? prev.formats.filter((item) => item !== format) : [...prev.formats, format];
      return { ...prev, formats: next.length ? next : ['pdf'] };
    });
  };

  const toggleWeekday = (day: number) => {
    setForm((prev) => {
      const exists = prev.weekdays.includes(day);
      const weekdays = exists ? prev.weekdays.filter((item) => item !== day) : [...prev.weekdays, day].sort();
      return { ...prev, weekdays: weekdays.length ? weekdays : [1] };
    });
  };

  const buildPayload = () => ({
    frequencyType: form.frequencyType,
    timezone: form.timezone,
    hour: form.frequencyType === 'cron' ? undefined : Number(form.hour),
    minute: form.frequencyType === 'cron' ? undefined : Number(form.minute),
    weekdays: form.frequencyType === 'weekly' ? form.weekdays : undefined,
    cronExpression: form.frequencyType === 'cron' ? form.cronExpression : undefined,
    recipientEmails: form.recipientEmails,
    isActive: form.isActive,
    report: {
      reportType: form.reportType,
      dateWindow: {
        strategy: form.dateWindowStrategy,
        days: form.dateWindowStrategy === 'last_n_days' ? Number(form.days) : undefined,
        startDate: form.dateWindowStrategy === 'fixed_range' ? form.startDate : undefined,
        endDate: form.dateWindowStrategy === 'fixed_range' ? form.endDate : undefined,
      },
      delivery: {
        formats: form.formats,
        mode: form.mode,
      },
    },
  });

  const saveSchedule = async () => {
    if (!form.recipientEmails.length) {
      setError('Add at least one recipient email');
      return;
    }

    setError(null);
    setLoading(true);
    try {
      const payload = buildPayload();
      if (editingId) {
        await sdk.client.put(`/api/v1/automation/reports/schedules/${editingId}`, payload);
      } else {
        await sdk.client.post('/api/v1/automation/reports/schedules', payload);
      }
      setForm(defaultFormState);
      setEditingId(null);
      await fetchSchedules();
    } catch (e: any) {
      setError(resolveErrorMessage(e, 'Failed to save schedule'));
    } finally {
      setLoading(false);
    }
  };

  const runNow = async (id: string) => {
    setLoading(true);
    try {
      await sdk.client.post(`/api/v1/automation/reports/run/${id}`, {});
      await fetchSchedules();
    } catch (e: any) {
      setError(resolveErrorMessage(e, 'Failed to run schedule'));
      setLoading(false);
    }
  };

  const deleteSchedule = async (id: string) => {
    setLoading(true);
    try {
      await sdk.client.delete(`/api/v1/automation/reports/schedules/${id}`);
      await fetchSchedules();
    } catch (e: any) {
      setError(resolveErrorMessage(e, 'Failed to delete schedule'));
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Scheduled Reports</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Manage donation and expense report schedules with multi-recipient delivery.
        </p>
      </div>

      {error ? <div className="text-sm text-red-600 dark:text-red-400">{error}</div> : null}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
          <h3 className="font-medium text-gray-900 dark:text-gray-100">{editingId ? 'Edit Schedule' : 'New Schedule'}</h3>
          <div className="grid grid-cols-2 gap-3">
            <select
              className="form-select"
              value={form.frequencyType}
              onChange={(e) => setForm((prev) => ({ ...prev, frequencyType: e.target.value as FormState['frequencyType'] }))}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="cron">Cron</option>
            </select>
            <input
              className="form-input"
              value={form.timezone}
              onChange={(e) => setForm((prev) => ({ ...prev, timezone: e.target.value }))}
              placeholder="Timezone"
            />
          </div>

          {form.frequencyType === 'cron' ? (
            <input
              className="form-input"
              value={form.cronExpression}
              onChange={(e) => setForm((prev) => ({ ...prev, cronExpression: e.target.value }))}
              placeholder="Cron expression"
            />
          ) : (
            <input
              className="form-input"
              type="time"
              value={toTimeString(form.hour, form.minute)}
              onChange={(e) => {
                const parsed = parseTimeString(e.target.value);
                setForm((prev) => ({ ...prev, hour: parsed.hour, minute: parsed.minute }));
              }}
            />
          )}

          {form.frequencyType === 'weekly' ? (
            <div className="flex flex-wrap gap-2">
              {weekdayLabels.map((label, index) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => toggleWeekday(index)}
                  className={`px-2 py-1 rounded text-xs ${
                    form.weekdays.includes(index)
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          ) : null}

          <div className="grid grid-cols-2 gap-3">
            <select
              className="form-select"
              value={form.reportType}
              onChange={(e) => setForm((prev) => ({ ...prev, reportType: e.target.value as FormState['reportType'] }))}
            >
              <option value="donation">Donations</option>
              <option value="expense">Expenses</option>
              <option value="both">Both</option>
            </select>
            <select
              className="form-select"
              value={form.dateWindowStrategy}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, dateWindowStrategy: e.target.value as FormState['dateWindowStrategy'] }))
              }
            >
              <option value="last_n_days">Last N Days</option>
              <option value="fixed_range">Fixed Range</option>
            </select>
          </div>

          {form.dateWindowStrategy === 'last_n_days' ? (
            <input
              className="form-input"
              type="number"
              min={1}
              max={365}
              value={form.days}
              onChange={(e) => setForm((prev) => ({ ...prev, days: Number(e.target.value) }))}
              placeholder="Days"
            />
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <input
                className="form-input"
                type="date"
                value={form.startDate}
                onChange={(e) => setForm((prev) => ({ ...prev, startDate: e.target.value }))}
              />
              <input
                className="form-input"
                type="date"
                value={form.endDate}
                onChange={(e) => setForm((prev) => ({ ...prev, endDate: e.target.value }))}
              />
            </div>
          )}

          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Delivery formats</p>
            <div className="flex gap-2">
              {(['pdf', 'excel', 'csv'] as const).map((format) => (
                <button
                  key={format}
                  type="button"
                  onClick={() => toggleFormat(format)}
                  className={`px-3 py-1 rounded text-xs ${
                    form.formats.includes(format)
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
                  }`}
                >
                  {format.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <select
            className="form-select"
            value={form.mode}
            onChange={(e) => setForm((prev) => ({ ...prev, mode: e.target.value as FormState['mode'] }))}
          >
            <option value="attachment">Attachment</option>
            <option value="link">Download Link</option>
            <option value="both">Both</option>
          </select>

          <div className="space-y-2">
            <div className="flex gap-2">
              <input
                className="form-input flex-1"
                value={form.recipientInput}
                onChange={(e) => setForm((prev) => ({ ...prev, recipientInput: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addRecipient();
                  }
                }}
                placeholder="recipient@example.com"
              />
              <button className="btn btn-primary px-3 py-2" type="button" onClick={addRecipient}>
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {form.recipientEmails.map((email) => (
                <button
                  key={email}
                  type="button"
                  className="px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-xs"
                  onClick={() => removeRecipient(email)}
                >
                  {email} x
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <button className="btn btn-primary px-4 py-2" type="button" onClick={saveSchedule} disabled={loading}>
              {editingId ? 'Update Schedule' : 'Create Schedule'}
            </button>
            {editingId ? (
              <button
                className="btn btn-secondary px-4 py-2"
                type="button"
                onClick={() => {
                  setEditingId(null);
                  setForm(defaultFormState);
                }}
              >
                Cancel Edit
              </button>
            ) : null}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
          <h3 className="font-medium text-gray-900 dark:text-gray-100">Existing Schedules</h3>
          {loading ? <p className="text-sm text-gray-500">Loading...</p> : null}
          {!loading && !sortedSchedules.length ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No schedules configured yet.</p>
          ) : null}
          <div className="space-y-2 max-h-[560px] overflow-auto">
            {sortedSchedules.map((schedule) => {
              const scheduleId = schedule._id || schedule.id;
              if (!scheduleId) return null;
              return (
              <div key={scheduleId} className="border border-gray-200 dark:border-gray-700 rounded p-3">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {schedule.reportConfig?.reportType || 'both'} reports
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Next run: {schedule.nextRunAt ? new Date(schedule.nextRunAt).toLocaleString() : 'N/A'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Recipients: {schedule.recipientEmails.join(', ')}
                </p>
                <div className="mt-2 flex gap-2">
                  <button className="btn btn-secondary px-3 py-1.5" type="button" onClick={() => runNow(scheduleId)}>
                    Run Now
                  </button>
                  <button
                    className="btn btn-secondary px-3 py-1.5"
                    type="button"
                    onClick={() => {
                      setEditingId(scheduleId);
                      setForm({
                        frequencyType: schedule.frequencyType,
                        timezone: schedule.timezone,
                        hour: schedule.hour ?? 9,
                        minute: schedule.minute ?? 0,
                        weekdays: schedule.weekdays ?? [1],
                        cronExpression: schedule.cronExpression ?? '0 9 * * *',
                        recipientEmails: schedule.recipientEmails ?? [],
                        recipientInput: '',
                        reportType: schedule.reportConfig?.reportType ?? 'both',
                        dateWindowStrategy: schedule.reportConfig?.dateWindow.strategy ?? 'last_n_days',
                        days: schedule.reportConfig?.dateWindow.days ?? 7,
                        startDate: schedule.reportConfig?.dateWindow.startDate?.slice(0, 10) || '',
                        endDate: schedule.reportConfig?.dateWindow.endDate?.slice(0, 10) || '',
                        formats: schedule.reportConfig?.delivery.formats ?? ['pdf'],
                        mode: schedule.reportConfig?.delivery.mode ?? 'attachment',
                        isActive: schedule.isActive,
                      });
                    }}
                  >
                    Edit
                  </button>
                  <button className="btn btn-secondary px-3 py-1.5" type="button" onClick={() => deleteSchedule(scheduleId)}>
                    Delete
                  </button>
                </div>
              </div>
            )})}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduledReports;
